// View options' column list, in current order. A reorderable row begins with a grip that moves its column by
// pointer drag or by Alt+ArrowUp and Alt+ArrowDown; a hideable row ends with its visibility toggle. Each
// change commits at once.

import React, { useId, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { Button, ToggleButton } from '@CommonComponents/buttons'
import {
  VIEW_COPY,
  movedText,
  reorderLabel,
  showLabel,
  visibilityFailed,
  visibilityResult,
} from './copy'
import {
  isHideable,
  isReorderable,
  listedColumns,
  moveColumn,
  placeColumn,
  reorderPosition,
  visibilityHidden,
} from './view'

const announce = message => {
  const runtime = typeof window !== 'undefined' ? window.MEButtons : null
  if (runtime && typeof runtime.announce === 'function')
    runtime.announce(message)
}

// the reorderable row under the pointer, and whether the drop lands after it
const dropTarget = (rows, key, clientY) => {
  let target = null
  rows.forEach((row, rowKey) => {
    if (target || !row) return
    const rect = row.getBoundingClientRect()
    if (clientY >= rect.top && clientY < rect.bottom)
      target = { key: rowKey, after: clientY >= rect.top + rect.height / 2 }
  })
  return target && target.key !== key ? target : null
}

export const ColumnList = ({ columns, view, commit }) => {
  const listed = listedColumns(columns, view)
  const hintId = useId()
  const rows = useRef(new Map())
  const grips = useRef(new Map())
  const refocus = useRef(null)
  const drag = useRef(null)
  const [drop, setDrop] = useState(null)

  // a moved row re-renders in its new place; its grip keeps focus
  useLayoutEffect(() => {
    const key = refocus.current
    if (!key) return
    refocus.current = null
    const grip = grips.current.get(key)
    if (grip && document.activeElement !== grip) grip.focus()
  })

  if (!listed.length) return null

  const reorder = (column, order) => {
    commit({ order })
    const { position, count } = reorderPosition(order, column.key, columns)
    announce(movedText(column, position, count))
  }

  const reorderable = () => {
    const out = new Map()
    listed.forEach(column => {
      if (isReorderable(column))
        out.set(column.key, rows.current.get(column.key))
    })
    return out
  }

  const gripHandlers = column => ({
    onKeyDown: event => {
      if (!event.altKey || event.ctrlKey || event.metaKey) return
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
      event.preventDefault()
      event.stopPropagation()
      refocus.current = column.key
      reorder(
        column,
        moveColumn(
          view.order,
          column.key,
          event.key === 'ArrowUp' ? -1 : 1,
          columns
        )
      )
    },
    onPointerDown: event => {
      if (event.button !== 0) return
      event.preventDefault()
      const target = event.currentTarget
      if (typeof target.setPointerCapture === 'function')
        target.setPointerCapture(event.pointerId)
      target.focus()
      drag.current = { key: column.key, drop: null }
    },
    onPointerMove: event => {
      if (!drag.current) return
      const next = dropTarget(reorderable(), column.key, event.clientY)
      const current = drag.current.drop
      if (
        (next &&
          current &&
          next.key === current.key &&
          next.after === current.after) ||
        next === current
      )
        return
      drag.current.drop = next
      setDrop(next)
    },
    onPointerUp: () => {
      const current = drag.current
      drag.current = null
      setDrop(null)
      if (!current || !current.drop) return
      refocus.current = column.key
      reorder(
        column,
        placeColumn(
          view.order,
          column.key,
          current.drop.key,
          current.drop.after,
          columns
        )
      )
    },
    onPointerCancel: () => {
      drag.current = null
      setDrop(null)
    },
  })

  return (
    <>
      <p id={hintId} className="sr-only">
        {VIEW_COPY.reorderHint}
      </p>
      <ul className="me-table-view-columns" aria-label={VIEW_COPY.columns}>
        {listed.map(column => {
          const visible = !view.hidden.includes(column.key)
          const dropping = drop && drop.key === column.key
          const classes = ['me-panel-row', 'me-table-view-column']
          if (!visible) classes.push('is-hidden')
          if (dropping)
            classes.push(drop.after ? 'is-drop-after' : 'is-drop-before')
          return (
            <li
              key={column.key}
              ref={node => rows.current.set(column.key, node)}
              className={classes.join(' ')}
              data-column-key={column.key}
            >
              {isReorderable(column) ? (
                <Button
                  ref={node => grips.current.set(column.key, node)}
                  className="me-btn-icon me-btn-icon-xs me-table-view-grip"
                  icon="fa-solid fa-grip-dots-vertical"
                  aria-label={reorderLabel(column)}
                  aria-describedby={hintId}
                  data-tooltip={reorderLabel(column)}
                  {...gripHandlers(column)}
                />
              ) : (
                <span className="me-table-view-grip-space" aria-hidden="true" />
              )}
              <span className="me-table-view-name">{column.label}</span>
              {isHideable(column) && (
                <ToggleButton
                  iconOnly
                  pressedFill={false}
                  className="me-btn-icon me-btn-icon-sm me-btn-icon-neutral"
                  pressed={visible}
                  data-pressed-icon="fa-regular fa-eye"
                  data-unpressed-icon="fa-regular fa-eye-slash"
                  data-success-label={visibilityResult(column, !visible)}
                  data-failure-label={visibilityFailed(column)}
                  onChange={next =>
                    commit({
                      hidden: visibilityHidden(view, column.key, next),
                    })
                  }
                >
                  {showLabel(column)}
                </ToggleButton>
              )}
            </li>
          )
        })}
      </ul>
    </>
  )
}

ColumnList.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  view: PropTypes.shape({
    order: PropTypes.arrayOf(PropTypes.string).isRequired,
    hidden: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  commit: PropTypes.func.isRequired,
}
